/**
 * The Y Combinator application as question data (LEV-2442), modeled the way
 * Techstars already is (`./techstars.ts`) and built by the same shared
 * question-data builder.
 *
 * Every YC draft written before YC moved onto that builder carries the seven
 * scored labels below as its `## ` headings, and splitAnswersByQuestion and
 * the section map match drafts to questions by heading. Rename a scored label
 * only with its old wording kept as an `aliases` entry, or every stored
 * draft's answer and score for it is orphaned.
 *
 * The six basics feed Submit's "Your details". Not verified against the live
 * form on 2026-10-19: apply.ycombinator.com sits behind a YC login. Re-check
 * them there and drop any the form does not ask for.
 *
 * The founder video's label and rubric are Lev's wording, read from YC's
 * public video page (checked 2026-10-20), not the form's: the form sits
 * behind the same login.
 *
 * Character limits are Lev's own approximations, not YC's: the Oct 2026
 * program-knowledge research pass found no official page that publishes
 * them, so every limit here is `verified: false`, the same flag techstars.ts
 * uses for its own unverified video-length limits.
 */
import { STEP_IDS } from "@/constants/steps";

import type { ApplicationQuestion } from "./types";

const BASIC_FIELD_RUBRIC = {
  tenOutOfTen: ["Filled in, current, and the same everywhere it appears."],
  commonMistakes: ["Leaving it blank. A blank field reads as a founder who did not finish."],
  reviewersWeigh: "Completeness. A finished form is the least a partner expects.",
};

export const YC_QUESTIONS: readonly ApplicationQuestion[] = [
  // --- Founders: basics ----------------------------------------------------
  {
    id: "founder-name",
    part: "founder-profile",
    label: "Founder name",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "completeness",
    detailKey: "founderName",
    missingNote: "Add your full name as it should appear on your {programName} application.",
    rubric: BASIC_FIELD_RUBRIC,
  },
  {
    id: "founder-email",
    part: "founder-profile",
    label: "Email",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "founderEmail",
    missingNote: "Add the email address {programName} should write to.",
    rubric: BASIC_FIELD_RUBRIC,
  },
  {
    id: "founder-linkedin",
    part: "founder-profile",
    label: "LinkedIn profile",
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "completeness",
    detailKey: "founderLinkedin",
    missingNote: "Paste your LinkedIn profile URL.",
    rubric: {
      tenOutOfTen: ["A current profile whose history backs up the founders answer."],
      commonMistakes: ["A stale profile that contradicts the roles or dates in the application."],
      reviewersWeigh: "Track record. Partners check that your background supports your claims.",
    },
  },
  {
    id: "founder-location",
    part: "founder-profile",
    label: "Location",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "founderLocation",
    missingNote: "Add the city you work from today.",
    rubric: BASIC_FIELD_RUBRIC,
  },

  // --- Company: basics -----------------------------------------------------
  {
    id: "company-name",
    part: "company",
    label: "Company name",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "companyName",
    missingNote: "Add your company's name as it appears on your site and deck.",
    rubric: BASIC_FIELD_RUBRIC,
  },
  {
    id: "company-url",
    part: "company",
    label: "Company URL",
    sourceSteps: [],
    scoring: "completeness",
    detailKey: "companyUrl",
    missingNote: "Paste your company's website address, if you have one.",
    rubric: {
      tenOutOfTen: ["A site that loads and says in one line what you do and for whom."],
      commonMistakes: ["A dead link, a parked domain, or a page of buzzwords."],
      reviewersWeigh: "Partners click it. It is part of your first impression.",
    },
  },

  // --- Company: scored -----------------------------------------------------
  {
    id: "what-you-make",
    part: "company",
    label: "What is your company going to make?",
    limit: { unit: "characters", max: 400, verified: false },
    sourceSteps: [STEP_IDS.IDEA, STEP_IDS.PRODUCT_BUILD],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "States what it does in the first sentence, in the simplest possible terms.",
        "Specific and concrete, not visionary.",
      ],
      commonMistakes: [
        "A description vague enough to describe any technology company.",
        "Marketing-speak instead of a plain description of the product.",
      ],
      reviewersWeigh:
        "Clarity. YC calls expressing yourself clearly probably the biggest thing applicants get wrong.",
    },
  },
  {
    id: "fifty-char-description",
    part: "company",
    label: "Describe what your company does in 50 characters or less",
    limit: { unit: "characters", max: 50, verified: false },
    sourceSteps: [STEP_IDS.IDEA],
    scoring: "scored",
    rubric: {
      tenOutOfTen: ["A sharp, specific outcome in one line, not a tagline."],
      commonMistakes: ["Generic positioning that could describe any company in the space."],
      reviewersWeigh: "The first impression: it is the one line every reader sees before anything else.",
    },
  },
  {
    id: "why-this-idea",
    part: "company",
    label: "Why did you pick this idea to work on?",
    limit: { unit: "characters", max: 500, verified: false },
    sourceSteps: [STEP_IDS.CUSTOMER_PROBLEM, STEP_IDS.TEAM],
    scoring: "scored",
    rubric: {
      tenOutOfTen: [
        "The founder's own domain expertise, stated plainly.",
        "How the founder knows people need this, from real users, not assumption.",
        "What the founder discovered by doing, not by reading.",
      ],
      commonMistakes: [
        "Describing the market instead of the founder's own path to the idea.",
        "No evidence of firsthand discovery.",
      ],
      reviewersWeigh:
        "Whether the founder found the idea by doing, from their own domain knowledge, rather than by reading about a market.",
    },
  },
  {
    id: "competitors",
    part: "company",
    label: "Who are your competitors?",
    limit: { unit: "characters", max: 400, verified: false },
    sourceSteps: [STEP_IDS.COMPETITIVE],
    scoring: "scored",
    rubric: {
      tenOutOfTen: ["Named competitors, plus what the founder understands about the business that they don't."],
      commonMistakes: [
        "Claiming no competitors exist.",
        "\"We're better than X\" instead of the contrarian insight YC is asking for.",
      ],
      reviewersWeigh: "The contrarian insight: what the founder knows that competitors don't.",
    },
  },
  {
    id: "how-make-money",
    part: "company",
    label: "How will you make money? How much could you make?",
    limit: { unit: "characters", max: 300, verified: false },
    sourceSteps: [STEP_IDS.GTM, STEP_IDS.TAM],
    scoring: "scored",
    rubric: {
      tenOutOfTen: ["A concrete pricing model and a best-estimate ceiling, stated with numbers."],
      commonMistakes: ["Abstract top-down market math instead of a concrete price and reachable customer count."],
      reviewersWeigh: "Whether the business model is concrete enough to size.",
    },
  },
  {
    id: "how-far-along",
    part: "company",
    label: "How far along are you?",
    limit: { unit: "characters", max: 600, verified: false },
    sourceSteps: [STEP_IDS.GTM, STEP_IDS.PRODUCT_BUILD],
    scoring: "scored",
    fedByFitStory: true,
    rubric: {
      tenOutOfTen: [
        "Specific users or numbers, not adjectives.",
        "Real revenue figures, or a plain statement that there are none yet.",
        "How long each founder has worked on it, and how much of that full-time.",
      ],
      commonMistakes: [
        "Adjectives (\"strong interest\", \"growing fast\") instead of real numbers.",
        "Vague or rounded figures instead of exact ones.",
      ],
      reviewersWeigh: "Evidence of speed: what happened in the last two weeks, not the whole backstory.",
    },
  },
  {
    id: "founders",
    part: "founder-profile",
    label: "Who are the founders?",
    limit: { unit: "characters", max: 500, verified: false },
    sourceSteps: [STEP_IDS.TEAM],
    scoring: "scored",
    fedByFitStory: true,
    rubric: {
      tenOutOfTen: ["Relevant experience only: what each founder shipped, scaled, or learned that bears on this idea."],
      commonMistakes: [
        "Generic founder achievements with no link to this idea.",
        "Listing the startup itself as the most impressive thing a founder has built.",
      ],
      reviewersWeigh:
        "Founder-market fit. YC says it cares more about the founders than the idea, and calls the founder question the most important question on the application.",
    },
  },

  // --- Required materials ----------------------------------------------------
  {
    id: "founder-video",
    part: "attachment",
    label: "One-minute founder video",
    limit: { unit: "seconds", max: 60, verified: false },
    sourceSteps: [STEP_IDS.TEAM, STEP_IDS.IDEA],
    scoring: "scored",
    fedByFitStory: true,
    founderStory: ["how the founders met", "how long you have worked together"],
    missingNote: "Record a one-minute video with every founder on camera.",
    sourceUrl: "https://www.ycombinator.com/video/",
    rubric: {
      tenOutOfTen: [
        "Every founder on camera, each saying who they are and what they do on the team.",
        "Talking, not reading. Partners want to see how the founders actually talk together.",
        "About a minute. A phone camera and one take are fine.",
      ],
      commonMistakes: [
        "A product demo or a pitch instead of the founders introducing themselves.",
        "A scripted, edited or over-produced video.",
        "Only one founder speaking.",
      ],
      reviewersWeigh: "The founders themselves, seen directly: how they talk and how they work together.",
    },
  },
];
